import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVerticalIcon, PencilSimpleIcon, StarIcon, TrashIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { cn } from "@/utils/utils";
import { FOCUS, HOVER, ICON_BUTTON } from "@/components/ui/recipes";
import { CATEGORY_PILL, PILL_BASE } from "@/components/cms/ui/pill-styles";
import { IconButton } from '@/components/ui/icon-button';
import { shownUrl } from '@/utils/localCopies';

interface SortableImageItemProps {
  image: { 
    id: string; 
    url: string; 
    imageCroppedUrl?: string;
  };
  index: number;
  variant: 'portrait' | 'landscape';
  onImageUpload: (id: string) => void;
  onRemoveImage: (id: string) => void;
}

export function SortableImageItem({ 
  image, 
  index, 
  variant,
  onImageUpload, 
  onRemoveImage 
}: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onImageUpload(image.id);
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemoveImage(image.id);
  };

  // Define dimensions based on variant - responsive sizing
  const containerClasses = variant === 'portrait' 
    ? 'w-full max-w-36 h-64' // 9:16 ratio, responsive width
    : 'w-full max-w-72 h-40 sm:w-64 sm:h-36'; // 16:9 ratio, smaller on mobile, fixed on sm+

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="relative group"
    >
      <div className={`${containerClasses} bg-lureo-neutral rounded-lureo-radius-lg overflow-hidden border-lureo-border-width border-lureo-line relative`}>
        {image.url ? (
          <div className="relative w-full h-full overflow-hidden">
            <img 
              src={shownUrl(image.imageCroppedUrl || image.url)}
              alt={`Header image ${image.id}`}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              aria-label="Replace image"
              className={cn("absolute inset-0 flex items-center justify-center bg-lureo-emphasis/40 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 cursor-pointer", FOCUS)}
              onClick={handleImageClick}
            >
              <PencilSimpleIcon className="w-6 h-6 text-lureo-on-emphasis" weight="fill" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={cn("w-full h-full flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors", HOVER, FOCUS)}
            onClick={handleImageClick}
          >
            <UploadSimpleIcon className="w-6 h-6 text-lureo-ink-subtle" />
            <span className="text-lureo-12 text-lureo-ink-subtle leading-4">Upload</span>
          </button>
        )}
      </div>
      
      {index === 0 && (
        <div className={cn(PILL_BASE, CATEGORY_PILL, "absolute top-2 left-2 z-10")}>
          <StarIcon className="w-3 h-3" weight="fill" />
          Main Cover
        </div>
      )}
      
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex gap-1 z-10">
        
        { variant === 'landscape' && <div 
          className={cn(ICON_BUTTON.overlay, "cursor-grab")}
          {...attributes}
          {...listeners}
        >
          <DotsSixVerticalIcon className="w-4 h-4" />
        </div>}

        {image.url && (
          <IconButton label="Remove image" tone="danger" onClick={handleRemoveClick}>
            <TrashIcon className="w-4 h-4" />
          </IconButton>
        )}
      </div>
    </div>
  );
}